import { Button, Flex, message } from 'antd';
import {
  FC,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import { IconDownload, IconZoomIn, IconZoomOut } from '@assets';
import { Loader } from '@components';
import { usePDFScaling } from '@hooks';
import {
  getFilePreviewKind,
  isPreviewBlocking,
  isPreviewPending,
  resolvePreviewSrc,
} from '@utils';

import { clampPanOffset } from './panOffset';
import * as S from './styled';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

type FileViewerFile = {
  id: string;
  url: string;
  name?: string; // Optional because TaskFile doesn't have name
  fileName?: string; // Some types have fileName instead of name
  mimeType?: string;
  fileSize?: number;
};

type FileViewerProps = {
  selectedFile?: FileViewerFile;
  loading?: boolean;
  pageWidth?: number;
  width?: string;
  height?: string;
  // When set, the document (PDF page or image) is scaled to fill the frame
  // width instead of a fixed pixel width, and the frame stretches to fill its
  // container. Used by the A4 document-detail preview; other callers keep the
  // legacy fixed-width behavior.
  fitToWidth?: boolean;
};

const getDownloadFileName = (file: FileViewerFile): string =>
  file.name || file.fileName || 'document.pdf';

const FileViewer: FC<FileViewerProps> = ({
  selectedFile,
  pageWidth,
  width,
  height,
  fitToWidth,
}) => {
  const [showLoader, setShowLoader] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [file, setFile] = useState<string | File | null>();
  // A HEIC preview that failed (or is a ready-without-url edge case): the raw HEIC
  // must not be fetched/rendered, so we show a download-only unavailable state.
  const [previewBlocked, setPreviewBlocked] = useState(false);
  const [frameWidth, setFrameWidth] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{ left: number; bottom: number } | null>(
    null
  );

  const isImage = getFilePreviewKind(selectedFile?.mimeType) === 'image';
  // For images, render the resolved preview (a JPEG for HEIC uploads); PDFs and
  // legacy files fall back to the original url via resolvePreviewSrc.
  const previewSrc = resolvePreviewSrc(selectedFile);

  const initialWidth = pageWidth || 405;
  const stepSize = 0.3;
  const magnifierMax = 4;

  const { scale, scaleUp, scaleDown, scaleReset } = usePDFScaling(
    initialWidth,
    stepSize,
    magnifierMax
  );

  // Drag-to-pan for zoomed images. `offset` is a screen-space translation that
  // is applied on top of the zoom scale; panning is only possible while zoomed.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const isPannable = isImage && scale > 1;

  // Clamp the pan so the scaled image can never be dragged past the frame edges
  // (its edge stays flush with the frame at most). transform-origin is centered,
  // so the max travel on each axis is half the overflow beyond the frame.
  const clampOffset = useCallback(
    (x: number, y: number) => {
      const img = imgRef.current;
      const stage = img?.parentElement;
      if (!img || !stage) return { x, y };

      return clampPanOffset(x, y, {
        contentWidth: img.offsetWidth,
        contentHeight: img.offsetHeight,
        frameWidth: stage.clientWidth,
        frameHeight: stage.clientHeight,
        scale,
      });
    },
    [scale]
  );

  // Re-clamp on zoom change so zooming out pulls the image back into view
  // (and resets to centered once there is nothing left to pan).
  useEffect(() => {
    setOffset((prev) => clampOffset(prev.x, prev.y));
  }, [clampOffset]);

  const handlePanStart = (event: ReactPointerEvent) => {
    if (!isPannable) return;
    panStart.current = { x: event.clientX, y: event.clientY, ox: offset.x, oy: offset.y };
    setIsPanning(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePanMove = (event: ReactPointerEvent) => {
    if (!panStart.current) return;
    setOffset(
      clampOffset(
        panStart.current.ox + (event.clientX - panStart.current.x),
        panStart.current.oy + (event.clientY - panStart.current.y)
      )
    );
  };

  const handlePanEnd = (event: ReactPointerEvent) => {
    if (!panStart.current) return;
    panStart.current = null;
    setIsPanning(false);
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  // In fit-to-width mode the PDF page is rendered at the frame's inner width so
  // it fills the A4 frame; zooming multiplies that base via `scale`.
  useLayoutEffect(() => {
    if (!fitToWidth) return;

    const node = containerRef.current;
    if (!node) return;

    const update = () => setFrameWidth(node.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [fitToWidth]);

  // Render the page slightly narrower than the frame so it never quite reaches
  // the edges. Otherwise a page that is a hair too tall triggers a scrollbar,
  // which steals width, which shrinks the frame, which hides the scrollbar — a
  // feedback loop that makes the document jump. The 5% margin exceeds a
  // scrollbar's width and breaks the loop.
  const pageBaseWidth =
    fitToWidth && frameWidth ? frameWidth * 0.95 : initialWidth;

  // Float the Zoom/Download panel against whichever is higher on screen: the
  // frame's bottom edge or the viewport's. The A4 frame is anchored to the
  // viewport height and sits below the page header, so it often extends past
  // the bottom of the screen — a panel pinned to the frame bottom would then be
  // scrolled out of reach. Clamping to the viewport keeps the controls visible.
  useEffect(() => {
    if (!fitToWidth) return;

    const update = () => {
      const el = viewerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const anchorY = Math.min(rect.bottom, window.innerHeight);
      setPanelPos({
        left: rect.left + rect.width / 2,
        bottom: window.innerHeight - anchorY + 30,
      });
    };
    update();

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    const observer = new ResizeObserver(update);
    if (viewerRef.current) observer.observe(viewerRef.current);

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, [fitToWidth, showLoader, file]);

  useEffect(
    () => {
      setShowLoader(true);
      setPreviewBlocked(false);
      if (!selectedFile) return;
      // A HEIC→JPEG preview is still being generated: keep the loader and don't
      // fetch (the raw HEIC url can't be rendered). Re-runs once it turns ready
      // because previewSrc is in the dep array below.
      if (isPreviewPending(selectedFile)) return;
      // A failed (or incomplete-ready) image preview has no resolvable source —
      // do NOT fall back to the raw HEIC url; show the download-only state.
      if (isImage && isPreviewBlocking(selectedFile)) {
        setShowLoader(false);
        setFile(null);
        setPreviewBlocked(true);
        return;
      }

      // Images fetch the resolved preview (JPEG for HEIC); PDFs the original.
      const fetchUrl = isImage ? previewSrc! : selectedFile.url;

      const controller = new AbortController();
      let objectUrl: string | null = null;

      fetch(fetchUrl, { signal: controller.signal })
        .then((response) => {
          setShowLoader(false);
          return response.blob();
        })
        .then((blob) => {
          objectUrl = URL.createObjectURL(blob);
          setFile(objectUrl);
          scaleReset();
          setOffset({ x: 0, y: 0 });
          setShowLoader(false);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          message.error('Error fetching PDF');
        });

      // Abort a stale request and revoke its object URL so a fast file switch
      // can't overwrite the latest preview or leak blobs.
      return () => {
        controller.abort();
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFile?.id, previewSrc]
  );

  const handleDownload = () => {
    if (!selectedFile) return;

    // Always download the ORIGINAL file, never the preview blob rendered in the
    // viewer (which may be a converted JPEG for a HEIC upload).
    const fileName = getDownloadFileName(selectedFile);

    fetch(selectedFile.url)
      .then((response) => response.blob())
      .then((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => {
        message.error('Error downloading file');
      });
  };

  return (
    <S.Viewer ref={viewerRef} $fill={fitToWidth}>
      <S.Wrapper width={width} height={height}>
        <S.DocumentContainer ref={containerRef} $fill={fitToWidth}>
          {showLoader ? (
            <Loader />
          ) : previewBlocked ? (
            <S.ImageStage>
              <Flex vertical align="center" gap={12}>
                <span>This file can't be previewed. Download it to view.</span>
                <Button
                  type="primary"
                  icon={<IconDownload />}
                  onClick={handleDownload}
                >
                  Download document file
                </Button>
              </Flex>
            </S.ImageStage>
          ) : isImage ? (
            <S.ImageStage>
              <img
                ref={imgRef}
                src={typeof file === 'string' ? file : undefined}
                alt={selectedFile?.name || selectedFile?.fileName || 'Document preview'}
                draggable={false}
                onPointerDown={handlePanStart}
                onPointerMove={handlePanMove}
                onPointerUp={handlePanEnd}
                onPointerCancel={handlePanEnd}
                style={{
                  transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                  transition: isPanning ? 'none' : undefined,
                  cursor: isPannable ? (isPanning ? 'grabbing' : 'grab') : undefined,
                }}
              />
            </S.ImageStage>
          ) : (
            <Document
              file={file}
              onLoadSuccess={({ numPages }) => {
                setPageCount(numPages);
              }}
              loading={<Loader />}
              error="Failed to load PDF file"
              noData=""
            >
              {Array.from({ length: pageCount }, (_, index) => (
                <S.PageContainer key={index}>
                  <Page
                    pageNumber={index + 1}
                    scale={scale}
                    loading=""
                    width={pageBaseWidth}
                  />
                </S.PageContainer>
              ))}
            </Document>
          )}
        </S.DocumentContainer>
      </S.Wrapper>

      {!showLoader && file && (
        <S.Panel
          align="center"
          className="preview_panel"
          $floating={fitToWidth}
          style={
            fitToWidth && panelPos
              ? { left: panelPos.left, bottom: panelPos.bottom }
              : undefined
          }
        >
          <S.PanelButton type="text" icon={<IconZoomIn />} onClick={scaleUp}>
            Zoom In
          </S.PanelButton>

          <S.PanelButton type="text" icon={<IconZoomOut />} onClick={scaleDown}>
            Zoom Out
          </S.PanelButton>

          <S.PanelButton
            type="text"
            icon={<IconDownload />}
            onClick={handleDownload}
          >
            Download
          </S.PanelButton>
        </S.Panel>
      )}
    </S.Viewer>
  );
};

export default FileViewer;